import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Progress } from '@/components/ui/Progress';
import { Modal, ModalFooter } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { LevelPlayground } from '@/features/tutorial/LevelPlayground';
import { Clock, Lock, CheckCircle, Unlock, Lightbulb, Sparkles, Key, AlertCircle, Download, FileText } from 'lucide-react';
import { useGameStore } from '@/stores/gameStore';
import { useUserStore } from '@/stores/userStore';

interface Puzzle {
  id: string;
  type: string;
  title: string;
  description: string;
  difficulty: number;
  hint: string;
  solution: string;
  completed: boolean;
}

interface Character {
  id: string;
  name: string;
  role: string;
  avatar: string;
  dialogue: any[];
}

interface EscapeRoom {
  id: string;
  name: string;
  theme: string;
  description: string;
  timeLimit: number;
  narrative: {
    intro: string;
    midpoint: string;
    completion: string;
  };
  puzzles: Puzzle[];
  files: Record<string, any>;
  characters: Character[];
  tools: string[];
  xpReward: number;
}

interface EscapeRoomContainerProps {
  roomData: EscapeRoom;
}

interface CodeDiscovery {
  puzzleId: string;
  code: string;
  message: string;
}

export function EscapeRoomContainer({ roomData }: EscapeRoomContainerProps) {
  const navigate = useNavigate();
  const [puzzles, setPuzzles] = useState(roomData.puzzles);
  const [timeRemaining, setTimeRemaining] = useState(roomData.timeLimit);
  const [isStarted, setIsStarted] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [showHint, setShowHint] = useState<string | null>(null);
  const [currentDialogue, setCurrentDialogue] = useState(roomData.narrative.intro);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showCharacterDialog, setShowCharacterDialog] = useState(true);
  const [recentlyCompleted, setRecentlyCompleted] = useState<string | null>(null);
  const [pulsingLocks, setPulsingLocks] = useState<Set<string>>(new Set());
  const [discoveredCodes, setDiscoveredCodes] = useState<Map<string, string>>(new Map());
  const [showCodeDiscovery, setShowCodeDiscovery] = useState<CodeDiscovery | null>(null);
  const [showCodeEntry, setShowCodeEntry] = useState<string | null>(null);
  const [codeInput, setCodeInput] = useState('');
  const [codeInput2, setCodeInput2] = useState('');
  const [codeError, setCodeError] = useState('');
  const [securityFilesOpened, setSecurityFilesOpened] = useState<Set<string>>(new Set());
  const [editedFiles, setEditedFiles] = useState<Map<string, string>>(new Map());
  const [earnedXP, setEarnedXP] = useState(0);
  const [bonusXP, setBonusXP] = useState(0);
  const [currentHintStep, setCurrentHintStep] = useState(0);

  const { addXP, syncRoomProgress, updateProgress, progress: userProgress } = useUserStore();

  const completedCount = puzzles.filter((p) => p.completed).length;
  const totalCount = puzzles.length;
  const progress = (completedCount / totalCount) * 100;

  // Check if this is a non-technical escape room
  const isNonTechnicalRoom = roomData.id === 'detectives-case' || roomData.id === 'treasure-quest';

  // Timer countdown
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isStarted && timeRemaining > 0 && !isCompleted) {
      interval = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            handleTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [isStarted, timeRemaining, isCompleted]);

  // Check completion
  useEffect(() => {
    if (completedCount === totalCount && completedCount > 0 && !isCompleted) {
      handleComplete();
    } else if (completedCount >= Math.floor(totalCount / 2) && completedCount < totalCount) {
      setCurrentDialogue(roomData.narrative.midpoint);
      setShowCharacterDialog(true);
    }
  }, [completedCount, totalCount]);

  // Clear recently completed animation
  useEffect(() => {
    if (recentlyCompleted) {
      const timer = setTimeout(() => setRecentlyCompleted(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [recentlyCompleted]);

  // Reset hint step when opening hint modal
  useEffect(() => {
    if (showHint !== null) {
      setCurrentHintStep(0);
    }
  }, [showHint]);

  // Auto-dismiss code discovery notification
  useEffect(() => {
    if (showCodeDiscovery) {
      const timer = setTimeout(() => setShowCodeDiscovery(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [showCodeDiscovery]);

  const handleStart = () => {
    setIsStarted(true);
    setShowIntro(false);
    setShowCharacterDialog(true);
  };

  const handleTimeUp = () => {
    alert("Time's up! The vault remains locked.");
  };

  const handleComplete = () => {
    setIsCompleted(true);
    setCurrentDialogue(roomData.narrative.completion);

    // Calculate time-based XP bonus
    // The earlier you finish, the more XP you get
    const timeRemainingPercent = timeRemaining / roomData.timeLimit;
    const calculatedBonusXP = Math.floor(roomData.xpReward * timeRemainingPercent);
    const totalXP = roomData.xpReward + calculatedBonusXP;

    // Check if escape room was already completed
    const previousProgress = userProgress.escapeRoomProgress.find(
      (p) => p.roomId === roomData.id
    );
    const wasAlreadyCompleted = previousProgress?.completed || false;

    setBonusXP(calculatedBonusXP);
    setEarnedXP(totalXP);

    // Award XP only if not previously completed
    if (!wasAlreadyCompleted) {
      addXP(totalXP);
    }

    // Calculate time taken (in seconds)
    const timeTaken = roomData.timeLimit - timeRemaining;

    // Save progress to database with completion time and puzzle count
    syncRoomProgress(roomData.id, {
      roomId: roomData.id,
      completed: true,
      completedAt: new Date(),
      bestTime: timeTaken,
      puzzlesCompleted: totalCount,
    });

    // Also update local progress for backward compatibility
    updateProgress(roomData.id, {
      levelId: roomData.id,
      completed: true,
      stars: 3,
      completedAt: new Date(),
      attempts: 1,
    });
  };

  const handlePuzzleSolve = (puzzleId: string) => {
    const puzzle = puzzles.find(p => p.id === puzzleId);
    if (!puzzle || puzzle.completed) return;

    setPuzzles((prev) =>
      prev.map((p) => (p.id === puzzleId ? { ...p, completed: true } : p))
    );
    setRecentlyCompleted(puzzleId);

    // Add pulse effect to remaining locks
    const remaining = puzzles.filter(p => p.id !== puzzleId && !p.completed);
    if (remaining.length > 0) {
      setPulsingLocks(new Set(remaining.map(p => p.id)));
      setTimeout(() => setPulsingLocks(new Set()), 1000);
    }
  };

  const handleCodeEntry = (puzzleId: string) => {
    setShowCodeEntry(puzzleId);
    setCodeInput('');
    setCodeInput2('');
    setCodeError('');
  };

  const handleCodeSubmit = () => {
    if (!showCodeEntry) return;

    const puzzle = puzzles.find(p => p.id === showCodeEntry);
    if (!puzzle) return;

    // For non-technical rooms, check against the puzzle's solution directly
    if (isNonTechnicalRoom) {
      const normalizedInput = codeInput.trim().toUpperCase().replace(/\s+/g, ' ');
      const normalizedSolution = puzzle.solution.toUpperCase().replace(/\s+/g, ' ');

      if (normalizedInput === normalizedSolution) {
        // Correct code!
        setShowCodeEntry(null);
        setCodeInput('');
        setCodeError('');
        handlePuzzleSolve(showCodeEntry);
      } else {
        // Wrong code
        setCodeError('Incorrect code. Check the document again using Claude AI or click the Hint button for guidance.');
      }
      return;
    }

    // For technical rooms, use the discovered codes system
    const discoveredCode = discoveredCodes.get(showCodeEntry);

    // File Vault Puzzle 3 requires two codes
    if (roomData.id === 'file-vault' && showCodeEntry === 'puzzle-3') {
      const normalizedInput1 = codeInput.trim().toUpperCase().replace(/\s+/g, ' ');
      const normalizedInput2 = codeInput2.trim().toUpperCase().replace(/\s+/g, ' ');

      // Expected codes
      const code1 = 'GAMMA-3344';
      const code2 = 'DELTA-5566';

      // Check if both codes match (in either order)
      const match1 = (normalizedInput1 === code1 && normalizedInput2 === code2) ||
                     (normalizedInput1 === code2 && normalizedInput2 === code1);

      if (match1) {
        // Correct codes!
        setShowCodeEntry(null);
        setCodeInput('');
        setCodeInput2('');
        setCodeError('');
        handlePuzzleSolve(showCodeEntry);
      } else {
        // Wrong codes
        setCodeError('Incorrect codes. Both security codes must be correct.');
      }
    } else {
      // Single code puzzles
      const normalizedInput = codeInput.trim().toUpperCase().replace(/\s+/g, ' ');
      const normalizedCode = discoveredCode?.toUpperCase().replace(/\s+/g, ' ');

      if (normalizedInput === normalizedCode) {
        // Correct code!
        setShowCodeEntry(null);
        setCodeInput('');
        setCodeInput2('');
        setCodeError('');
        handlePuzzleSolve(showCodeEntry);
      } else {
        // Wrong code
        setCodeError('Incorrect code. Try again or explore more files.');
      }
    }
  };

  // Validation functions for Editor's Chamber
  const validateSyntaxFix = (content: string): boolean => {
    // Check if all syntax errors are fixed
    const checks = [
      content.includes('function calculateTotal(items)'), // Fixed missing )
      content.includes('let total = 0;'), // Fixed missing ;
      content.includes('i < items.length; i++)'), // Fixed missing )
      content.includes('total: total,'), // Fixed missing ,
    ];
    return checks.every(check => check);
  };

  const validateRefactor = (content: string): boolean => {
    // Check if code has been broken into multiple functions
    const functionMatches = content.match(/function\s+\w+/g) || [];
    if (functionMatches.length < 3) return false;

    // Check for key refactoring patterns
    const hasValidation = /validate|check/i.test(content);
    const hasFormatting = /format|process/i.test(content);
    const hasCalculation = /calculate|tier/i.test(content);

    return hasValidation && (hasFormatting || hasCalculation);
  };

  const validateTypeAnnotations = (content: string): boolean => {
    // Check for type annotations on parameters and return types
    const typeAnnotations = content.match(/:\s*(number|string|Item\[\])/g) || [];

    // Should have at least 6 type annotations (3 functions with params and return types)
    if (typeAnnotations.length < 6) return false;

    // Check that all three functions have some type annotations
    const hasAddTypes = /add\([^)]*:\s*number/.test(content);
    const hasGreetTypes = /greet\([^)]*:\s*string/.test(content);
    const hasProcessTypes = /processItems\([^)]*:\s*Item\[\]/.test(content);

    return hasAddTypes && hasGreetTypes && hasProcessTypes;
  };

  // Handle actions from the playground to check for puzzle solutions
  const handlePlaygroundAction = (action: string, data: any) => {
    console.log('Investigation action:', action, data);

    // Track file edits for Editor's Chamber validation
    if (action === 'file_edit' && data.file && data.content !== undefined) {
      setEditedFiles(prev => new Map(prev).set(data.file, data.content));
    }

    // FILE VAULT ROOM - Only trigger code discovery on 'read' and 'file_opened' actions
    if (roomData.id === 'file-vault') {
      if (action !== 'read' && action !== 'file_opened') {
        return;
      }

      puzzles.forEach((puzzle) => {
        if (puzzle.completed) return;
        if (discoveredCodes.has(puzzle.id)) return;

        let shouldDiscover = false;
        let codeFound = '';
        let message = '';
        const filePath = data.file || data.path;

        // Puzzle 1: Find README-SECRET.md in archives/old/
        if (puzzle.id === 'puzzle-1' && puzzle.solution === 'archives/old/README-SECRET.md') {
          if (filePath === 'archives/old/README-SECRET.md') {
            shouldDiscover = true;
            codeFound = 'ALPHA-7734';
            message = action === 'file_opened'
              ? 'You opened the secret file in the editor and found the code!'
              : 'You read the secret file and found the code!';
          }
        }

        // Puzzle 2: Find config.txt with PASSWORD
        if (puzzle.id === 'puzzle-2' && puzzle.solution === 'system/configs/backup/config.txt') {
          if (filePath === 'system/configs/backup/config.txt') {
            shouldDiscover = true;
            codeFound = 'BETA-9921';
            message = action === 'file_opened'
              ? 'You opened config.txt in the editor and found the password!'
              : 'You read the config file and found the password!';
          }
        }

        // Puzzle 3: Find BOTH JS files in security directory
        if (puzzle.id === 'puzzle-3' && puzzle.solution === 'security/') {
          if (filePath?.startsWith('security/') && filePath?.endsWith('.js')) {
            if (filePath.includes('authenticate.js') || filePath.includes('hash.js')) {
              const newFilesOpened = new Set(securityFilesOpened);
              if (filePath.includes('authenticate.js')) {
                newFilesOpened.add('authenticate.js');
              }
              if (filePath.includes('hash.js')) {
                newFilesOpened.add('hash.js');
              }
              setSecurityFilesOpened(newFilesOpened);

              if (newFilesOpened.has('authenticate.js') && newFilesOpened.has('hash.js')) {
                shouldDiscover = true;
                codeFound = 'GAMMA-3344,DELTA-5566';
                message = action === 'file_opened'
                  ? 'You opened both security files and discovered all authentication codes!'
                  : 'You read both security files and discovered all authentication codes!';
              }
            }
          }
        }

        if (shouldDiscover) {
          setDiscoveredCodes(prev => new Map(prev).set(puzzle.id, codeFound));
          setShowCodeDiscovery({
            puzzleId: puzzle.id,
            code: codeFound,
            message: message
          });
        }
      });
    }

    // EDITOR'S CHAMBER - Validate edited files
    if (roomData.id === 'editors-chamber' && action === 'file_edit') {
      puzzles.forEach((puzzle: any) => {
        if (puzzle.completed) return;
        if (discoveredCodes.has(puzzle.id)) return;

        let shouldDiscover = false;
        let codeFound = '';
        let message = '';

        // Puzzle 1: Syntax Purifier (corrupted.js)
        if (puzzle.id === 'puzzle-1' && data.file === 'corrupted.js') {
          if (validateSyntaxFix(data.content)) {
            shouldDiscover = true;
            codeFound = puzzle.unlockCode || 'SYNTAX-FIXED-2024';
            message = '✨ All syntax errors fixed! The code is now valid and reveals its secret.';
          }
        }

        // Puzzle 2: Code Simplifier (legacy.js)
        if (puzzle.id === 'puzzle-2' && data.file === 'legacy.js') {
          if (validateRefactor(data.content)) {
            shouldDiscover = true;
            codeFound = puzzle.unlockCode || 'REFACTOR-COMPLETE-777';
            message = '✨ Code successfully refactored into multiple functions! The complexity has been tamed.';
          }
        }

        // Puzzle 3: Type Annotator (untyped.ts)
        if (puzzle.id === 'puzzle-3' && data.file === 'untyped.ts') {
          if (validateTypeAnnotations(data.content)) {
            shouldDiscover = true;
            codeFound = puzzle.unlockCode || 'TYPES-SECURE-456';
            message = '✨ All functions properly typed! TypeScript is now protecting your code.';
          }
        }

        if (shouldDiscover) {
          setDiscoveredCodes(prev => new Map(prev).set(puzzle.id, codeFound));
          setShowCodeDiscovery({
            puzzleId: puzzle.id,
            code: codeFound,
            message: message
          });
        }
      });
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle file download for non-technical rooms - link to static assets
  const handleFileDownload = (fileName: string, roomId: string) => {
    // Create direct link to public assets
    const assetPath = `/escape-rooms/${roomId}/${fileName}`;
    const link = document.createElement('a');
    link.href = assetPath;
    link.download = fileName;
    link.target = '_blank'; // Open in new tab as fallback
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get downloadable files for non-technical rooms (exclude README and guide files)
  const getDownloadableFiles = () => {
    if (!isNonTechnicalRoom) return [];

    // Map original file paths to actual asset filenames
    // Some files have multiple format options for compatibility
    const fileMapping: Record<string, Record<string, Array<{ name: string; icon: string; label?: string }>>> = {
      'detectives-case': {
        'case-files/police-report.pdf': [{ name: 'police-report.pdf', icon: '📄' }],
        'case-files/witness-timeline.xlsx': [{ name: 'witness-timeline.csv', icon: '📊' }],
        'case-files/evidence-log.pdf': [{ name: 'evidence-log.txt', icon: '📄' }],
      },
      'treasure-quest': {
        'blackwood-documents/treasure-map.pdf': [{ name: 'treasure-map.png', icon: '🗺️' }],
        'blackwood-documents/trading-ledger.xlsx': [{ name: 'trading-ledger.csv', icon: '📊' }],
        'blackwood-documents/captains-diary.pdf': [
          { name: 'captains-diary.pdf', icon: '📖', label: 'PDF' },
          { name: 'captains-diary.txt', icon: '📝', label: 'TXT' },
        ],
      },
    };

    const mappings = fileMapping[roomData.id] || {};

    return Object.entries(roomData.files)
      .filter(([path]) => !path.includes('README') && !path.includes('guide'))
      .flatMap(([path]) => {
        const mapping = mappings[path];
        return mapping ? mapping.map(file => ({
          path,
          name: file.name,
          icon: file.icon,
          label: file.label,
        })) : [];
      });
  };

  // Parse puzzle description into steps for non-technical rooms
  const parseDescriptionIntoSteps = (description: string) => {
    const sections: Array<{ title: string; content: string; type: 'header' | 'list' | 'text' }> = [];

    // Split by main section headers (those with emojis or all caps)
    const parts = description.split(/\*\*([^*]+)\*\*/g);

    for (let i = 1; i < parts.length; i += 2) {
      let title = parts[i].trim();
      let content = parts[i + 1]?.trim() || '';

      // Skip if title or content is empty
      if (!title || !content) continue;

      // Skip puzzle title headers (like "PUZZLE 1: FIND THE ACCESS CODE")
      if (/^PUZZLE \d+:/i.test(title)) continue;

      // Aggressive cleanup of trailing numbers and artifacts
      // Remove lines that are just numbers or numbers with asterisks
      content = content
        .replace(/\n+\d+\.\s*\*\*[^*]*\*\*[^]*$/s, '') // Remove trailing numbered bold sections
        .replace(/\n+\d+\.\s*$/gm, '') // Remove lines with just "1." or "2." etc
        .replace(/\n+\d+$/gm, '') // Remove lines with just numbers
        .replace(/\n{3,}/g, '\n\n') // Normalize multiple newlines
        .trim();

      // Filter out very short content (likely artifacts)
      if (content.length < 20 && !content.includes('Expected')) continue;

      // Process numbered lists with bold sub-headers (like "1. **Open Claude**")
      if (/^\d+\.\s*\*\*/.test(content)) {
        // This is a numbered list with bold items - extract each numbered item
        const items = content.match(/\d+\.\s*\*\*[^*]+\*\*[^]*?(?=\n+\d+\.\s*\*\*|$)/gs);

        if (items && items.length > 0) {
          // Create a section for this group
          const listItems = items.map(item => {
            // Clean up the item
            let cleanItem = item
              .replace(/\n+\d+\.\s*\*\*[^*]*$/s, '') // Remove partial next item
              .replace(/\n+\d+\.\s*$/gm, '') // Remove trailing numbers
              .trim();

            // Extract the bold header and content
            const match = cleanItem.match(/\d+\.\s*\*\*([^*]+)\*\*\s*([\s\S]*)/);
            if (match) {
              const [, subTitle, subContent] = match;
              return `**${subTitle.trim()}**\n${subContent.trim()}`;
            }
            return cleanItem;
          })
          .filter(item => item.length > 10) // Remove very short items
          .join('\n\n');

          if (listItems.length > 10) { // Only add if we have meaningful content
            sections.push({
              title,
              content: listItems,
              type: 'list'
            });
          }
          continue;
        }
      }

      // Regular numbered list (without bold sub-headers)
      if (/^\d+\./.test(content)) {
        // Clean up trailing numbers
        content = content.replace(/\n+\d+\.\s*$/gm, '').trim();

        if (content.length > 20) {
          sections.push({
            title,
            content,
            type: 'list'
          });
        }
        continue;
      }

      // Regular text section
      if (content.length > 20) {
        sections.push({
          title,
          content,
          type: 'text'
        });
      }
    }

    // Filter out sections that are too short or empty after processing
    return sections.filter(section => {
      const contentLength = section.content.replace(/\s/g, '').length;
      return contentLength > 15; // At least 15 non-whitespace characters
    });
  };

  // Get steps for current puzzle in hint modal
  const getCurrentPuzzleSteps = () => {
    if (!showHint) return [];
    const puzzle = puzzles.find((p) => p.id === showHint);
    if (!puzzle) return [];
    return parseDescriptionIntoSteps(puzzle.description);
  };

  const timePercentage = (timeRemaining / roomData.timeLimit) * 100;
  const character = roomData.characters[0];

  // Get lock icon for puzzle based on state
  const getLockIcon = (puzzle: Puzzle) => {
    if (puzzle.completed) {
      return <Unlock className="text-success-400 animate-pulse" size={32} />;
    }
    if (discoveredCodes.has(puzzle.id)) {
      return <Key className="text-warning-400 animate-bounce" size={32} />;
    }
    if (pulsingLocks.has(puzzle.id)) {
      return <Lock className="text-warning-400 animate-bounce" size={32} />;
    }
    return <Lock className="text-primary-500" size={32} />;
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Code Discovery Notification */}
      {showCodeDiscovery && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <Card variant="elevated" className="border-2 border-success-600 bg-gradient-to-br from-success-900/40 to-dark-50 shadow-2xl animate-glow">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="text-4xl animate-bounce">🔑</div>
                <div>
                  <div className="font-bold text-success-400 mb-1">
                    {showCodeDiscovery.code.includes(',') ? 'Codes Discovered!' : 'Code Discovered!'}
                  </div>
                  <p className="text-primary-200 text-sm mb-2">{showCodeDiscovery.message}</p>
                  <div className="bg-dark-100 p-2 rounded border border-success-600/30 space-y-1">
                    {showCodeDiscovery.code.includes(',') ? (
                      showCodeDiscovery.code.split(',').map((code, idx) => (
                        <div key={idx}>
                          <code className="text-success-400 font-bold">{code}</code>
                        </div>
                      ))
                    ) : (
                      <code className="text-success-400 font-bold">{showCodeDiscovery.code}</code>
                    )}
                  </div>
                  <p className="text-primary-300 text-xs mt-2 italic">
                    Use "Enter Code{showCodeDiscovery.code.includes(',') ? 's' : ''}" button to unlock the puzzle
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Code Entry Modal */}
      <Modal
        isOpen={showCodeEntry !== null}
        onClose={() => {
          setShowCodeEntry(null);
          setCodeInput('');
          setCodeInput2('');
          setCodeError('');
        }}
        title={roomData.id === 'file-vault' && showCodeEntry === 'puzzle-3' ? '🔐 Enter Security Codes' : '🔐 Enter Security Code'}
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-primary-900/30 to-transparent p-4 rounded-lg border border-primary-600/30">
            <p className="text-primary-200 text-sm mb-2">
              {isNonTechnicalRoom
                ? 'Enter the code you found by analyzing the document with Claude AI.'
                : roomData.id === 'file-vault' && showCodeEntry === 'puzzle-3'
                ? 'Enter both security codes you discovered in the security files to unlock this puzzle.'
                : 'Enter the security code you discovered to unlock this puzzle.'}
            </p>
            {!isNonTechnicalRoom && showCodeEntry && discoveredCodes.has(showCodeEntry) && (
              <div className="text-xs text-primary-300 italic">
                💡 Hint: Check the {roomData.id === 'file-vault' && showCodeEntry === 'puzzle-3' ? 'codes' : 'code'} you found in the {roomData.id === 'editors-chamber' ? 'Code Editor' : 'Investigation Terminal'}
              </div>
            )}
          </div>

          {roomData.id === 'file-vault' && showCodeEntry === 'puzzle-3' ? (
            <>
              <Input
                label="First Security Code"
                value={codeInput}
                onChange={(e) => {
                  setCodeInput(e.target.value);
                  setCodeError('');
                }}
                placeholder="Enter first code (e.g., XXXX-0000)"
                error=""
                autoFocus
              />
              <Input
                label="Second Security Code"
                value={codeInput2}
                onChange={(e) => {
                  setCodeInput2(e.target.value);
                  setCodeError('');
                }}
                placeholder="Enter second code (e.g., XXXX-0000)"
                error=""
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCodeSubmit();
                  }
                }}
              />
            </>
          ) : (
            <Input
              label="Security Code"
              value={codeInput}
              onChange={(e) => {
                setCodeInput(e.target.value);
                setCodeError('');
              }}
              placeholder="Enter code (e.g., XXXX-0000)"
              error=""
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCodeSubmit();
                }
              }}
            />
          )}

          {codeError && (
            <div className="flex items-start gap-2 bg-error-900/20 border border-error-600/30 p-3 rounded-lg">
              <AlertCircle className="text-error-400 mt-0.5 flex-shrink-0" size={16} />
              <p className="text-error-300 text-sm">{codeError}</p>
            </div>
          )}

          {!isNonTechnicalRoom && !discoveredCodes.has(showCodeEntry || '') && (
            <div className="flex items-start gap-2 bg-warning-900/20 border border-warning-600/30 p-3 rounded-lg">
              <Lightbulb className="text-warning-400 mt-0.5 flex-shrink-0" size={16} />
              <p className="text-warning-300 text-sm">
                {roomData.id === 'file-vault' && showCodeEntry === 'puzzle-3'
                  ? "You haven't discovered all codes yet. Explore BOTH security files (authenticate.js and hash.js) in the Investigation Terminal!"
                  : roomData.id === 'editors-chamber'
                  ? "You haven't discovered this code yet. Edit the file correctly to reveal the code!"
                  : "You haven't discovered this code yet. Explore the files in the Investigation Terminal to find it!"}
              </p>
            </div>
          )}
          {isNonTechnicalRoom && (
            <div className="flex items-start gap-2 bg-primary-900/20 border border-primary-600/30 p-3 rounded-lg">
              <Lightbulb className="text-primary-400 mt-0.5 flex-shrink-0" size={16} />
              <p className="text-primary-300 text-sm">
                💡 Download the document, upload it to Claude AI at <a href="https://claude.ai" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300 underline">claude.ai</a>, and ask Claude to find the code. Need help? Click the "Hint" button for step-by-step instructions!
              </p>
            </div>
          )}
        </div>

        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              setShowCodeEntry(null);
              setCodeInput('');
              setCodeInput2('');
              setCodeError('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCodeSubmit}
            disabled={roomData.id === 'file-vault' && showCodeEntry === 'puzzle-3'
              ? (!codeInput.trim() || !codeInput2.trim())
              : !codeInput.trim()}
          >
            Submit {roomData.id === 'file-vault' && showCodeEntry === 'puzzle-3' ? 'Codes' : 'Code'} 🔓
          </Button>
        </ModalFooter>
      </Modal>

      {/* Intro Modal */}
      <Modal
        isOpen={showIntro}
        onClose={() => navigate('/escape-room')}
        title=""
        size="lg"
        closeOnOverlayClick={false}
      >
        <div className="space-y-6">
          {/* Cinematic Header */}
          <div className="text-center relative">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 via-primary-500/20 to-primary-600/20 blur-xl"></div>
            <div className="relative">
              <div className="text-8xl mb-4 animate-pulse">🔐</div>
              <h2 className="text-3xl font-bold mb-2 text-primary-400">
                {roomData.name}
              </h2>
              <Badge variant="info" size="lg" className="mb-4 text-base px-6 py-2">
                {roomData.theme}
              </Badge>
            </div>
          </div>

          {/* Story */}
          <div className="space-y-4">
            <div className="bg-gradient-to-br from-dark-100 to-dark-50 p-6 rounded-lg border-2 border-primary-600/30 shadow-lg">
              <p className="text-primary-200 text-lg leading-relaxed">
                {roomData.description}
              </p>
            </div>

            {/* Character Introduction */}
            {character && (
              <div className="flex items-start gap-4 bg-dark-100 p-4 rounded-lg border border-primary-600/20">
                <div className="text-5xl">{character.avatar}</div>
                <div className="flex-1">
                  <div className="font-semibold text-primary-400 mb-1">
                    {character.name}
                  </div>
                  <p className="text-primary-300 italic text-sm leading-relaxed">
                    "{currentDialogue}"
                  </p>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="bg-warning-900/20 border border-warning-600/30 p-4 rounded-lg">
              <div className="flex items-start gap-2">
                <Sparkles className="text-warning-400 mt-1 flex-shrink-0" size={20} />
                <div>
                  <div className="font-semibold text-warning-400 mb-1 text-sm">How to Play</div>
                  {isNonTechnicalRoom ? (
                    <>
                      <p className="text-primary-300 text-sm leading-relaxed mb-2">
                        Download documents, analyze them with Claude AI, and find the hidden codes!
                      </p>
                      <ul className="text-primary-300 text-xs space-y-1 list-disc list-inside">
                        <li>Download the case files using the download buttons</li>
                        <li>Go to <a href="https://claude.ai" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300 underline">claude.ai</a> or open Claude desktop app</li>
                        <li>Upload each document to Claude using the 📎 paperclip icon</li>
                        <li>Ask Claude to analyze the document (detailed instructions in hints)</li>
                        <li>Find the code hidden in each document</li>
                        <li>Enter the code to unlock each puzzle!</li>
                      </ul>
                      <p className="text-warning-300 text-xs mt-2 italic">
                        💡 No coding skills required! Just download, upload to Claude, and ask questions.
                      </p>
                    </>
                  ) : roomData.id === 'file-vault' ? (
                    <>
                      <p className="text-primary-300 text-sm leading-relaxed mb-2">
                        Explore files to discover hidden security codes, then enter them to unlock puzzles!
                      </p>
                      <ul className="text-primary-300 text-xs space-y-1 list-disc list-inside">
                        <li>Click files in the file tree to view them in the editor</li>
                        <li>Or use terminal commands: <code className="text-success-400">read</code>, <code className="text-success-400">glob</code>, <code className="text-success-400">grep</code></li>
                        <li>Use <code className="text-success-400">cd</code> to navigate directories</li>
                        <li>Codes auto-discover when you open files containing them</li>
                        <li>Click "Enter Code" button to unlock puzzles</li>
                      </ul>
                    </>
                  ) : roomData.id === 'editors-chamber' ? (
                    <>
                      <p className="text-primary-300 text-sm leading-relaxed mb-2">
                        Edit corrupted files to fix them. When your edits are correct, codes will be revealed!
                      </p>
                      <ul className="text-primary-300 text-xs space-y-1 list-disc list-inside">
                        <li>Click files in the tree to open them in the editor</li>
                        <li>Edit the code directly in the editor to fix issues</li>
                        <li>Read README.md for detailed guidance on each puzzle</li>
                        <li>When your fix is correct, the code auto-discovers!</li>
                        <li>Click "Enter Code" button to unlock each puzzle</li>
                      </ul>
                    </>
                  ) : (
                    <>
                      <p className="text-primary-300 text-sm leading-relaxed mb-2">
                        Complete challenges to discover unlock codes!
                      </p>
                      <ul className="text-primary-300 text-xs space-y-1 list-disc list-inside">
                        <li>Use the tools provided to solve each puzzle</li>
                        <li>Codes will be revealed when challenges are completed</li>
                        <li>Enter codes to unlock puzzles</li>
                      </ul>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Mission Brief */}
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-dark-100 rounded-lg border border-primary-600/20">
              <Clock className="mx-auto mb-2 text-primary-500" size={28} />
              <div className="text-xs text-primary-300 mb-1">Time Limit</div>
              <div className="text-xl font-bold text-primary-400">
                {Math.floor(roomData.timeLimit / 60)} min
              </div>
            </div>
            <div className="text-center p-4 bg-dark-100 rounded-lg border border-primary-600/20">
              <Lock className="mx-auto mb-2 text-primary-500" size={28} />
              <div className="text-xs text-primary-300 mb-1">Codes to Find</div>
              <div className="text-xl font-bold text-primary-400">
                {totalCount} codes
              </div>
            </div>
            <div className="text-center p-4 bg-dark-100 rounded-lg border border-primary-600/20">
              <Sparkles className="mx-auto mb-2 text-success-500" size={28} />
              <div className="text-xs text-primary-300 mb-1">Reward</div>
              <div className="text-xl font-bold text-success-400">
                {roomData.xpReward} XP
              </div>
            </div>
          </div>

          {/* Tools Available */}
          <div className="bg-dark-100 p-4 rounded-lg border border-primary-600/20">
            <div className="text-sm text-primary-300 mb-2 font-semibold">
              🛠️ Your Tools:
            </div>
            <div className="flex gap-2">
              {roomData.tools.map((tool) => (
                <Badge key={tool} variant="success" size="sm">
                  {tool}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        <ModalFooter>
          <Button
            variant="ghost"
            size="lg"
            onClick={() => navigate('/escape-room')}
          >
            ← Go Back
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleStart}
            className="flex-1 text-lg py-6 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-600"
          >
            Begin Your Quest →
          </Button>
        </ModalFooter>
      </Modal>

      {/* Main Game Screen */}
      {isStarted && (
        <>
          {/* Atmospheric Header */}
          <div className="mb-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-600/10 via-transparent to-primary-600/10"></div>
            <Card variant="elevated" className="relative border-2 border-primary-600/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-5xl">{character?.avatar || '🔐'}</div>
                    <div className="flex-1">
                      <CardTitle className="text-2xl mb-2">{roomData.name}</CardTitle>
                      {showCharacterDialog && (
                        <div className="flex items-start gap-2 bg-dark-100/50 p-3 rounded-lg border border-primary-600/20 animate-fade-in">
                          <Sparkles size={16} className="text-primary-500 mt-1 flex-shrink-0" />
                          <p className="text-primary-200 italic text-sm">"{currentDialogue}"</p>
                          <button
                            onClick={() => setShowCharacterDialog(false)}
                            className="text-primary-500 hover:text-primary-400 ml-2"
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-4xl font-bold mb-2 ${
                      timeRemaining < 300 ? 'text-error-400 animate-pulse' : 'text-primary-400'
                    }`}>
                      ⏱️ {formatTime(timeRemaining)}
                    </div>
                    <Badge variant={completedCount === totalCount ? 'success' : 'info'} size="lg">
                      {completedCount}/{totalCount} Unlocked
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Progress value={progress} showLabel className="h-3" />
                  <div className="flex justify-center gap-2">
                    {puzzles.map((puzzle) => (
                      <div
                        key={puzzle.id}
                        className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${
                          puzzle.completed
                            ? 'bg-success-600 border-success-400 scale-110'
                            : discoveredCodes.has(puzzle.id)
                            ? 'bg-warning-600 border-warning-400 animate-pulse'
                            : 'bg-dark-100 border-primary-600/30'
                        }`}
                      >
                        {puzzle.completed ? '✓' : discoveredCodes.has(puzzle.id) ? '🔑' : '🔒'}
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Interactive Investigation Area - Moved to top */}
          {isNonTechnicalRoom ? (
            /* File Downloads Section for Non-Technical Rooms */
            <Card variant="elevated" className="border-2 border-primary-600/30 mb-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">📥</div>
                    <div>
                      <CardTitle>Case Files</CardTitle>
                      <p className="text-sm text-primary-300 mt-1">
                        Download these documents and analyze them using Claude AI (claude.ai or desktop app)
                      </p>
                    </div>
                  </div>
                  <Badge variant="info" size="sm">
                    {getDownloadableFiles().length} files
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Instructions Card */}
                  <div className="bg-gradient-to-br from-primary-900/30 to-transparent p-4 rounded-lg border border-primary-600/30">
                    <div className="flex items-start gap-3">
                      <Lightbulb className="text-warning-400 mt-1 flex-shrink-0" size={20} />
                      <div className="flex-1">
                        <h4 className="font-semibold text-primary-300 mb-2">How to Use These Files:</h4>
                        <ol className="text-sm text-primary-200 space-y-1 list-decimal list-inside">
                          <li>Download a file by clicking the download button</li>
                          <li>Go to <a href="https://claude.ai" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300 underline">claude.ai</a> or open the Claude desktop app</li>
                          <li>Upload the file to Claude using the 📎 paperclip icon</li>
                          <li>Ask Claude to analyze the document (see hints for each puzzle)</li>
                          <li>Find the code hidden in the document</li>
                          <li>Enter the code to unlock the puzzle!</li>
                        </ol>
                      </div>
                    </div>
                  </div>

                  {/* File Download Buttons */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {getDownloadableFiles().map((file, idx) => (
                      <Card
                        key={`${file.path}-${file.name}-${idx}`}
                        variant="outline"
                        className="hover:border-primary-500/50 transition-all"
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="text-3xl">{file.icon}</div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-primary-300 text-sm truncate">
                                {file.name}
                              </div>
                              <div className="text-xs text-primary-400">
                                {file.path.split('/')[0]}
                                {file.label && ` • ${file.label}`}
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleFileDownload(file.name, roomData.id)}
                            className="w-full flex items-center justify-center gap-2"
                          >
                            <Download size={14} />
                            Download {file.label || ''}
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Additional Help */}
                  <div className="bg-dark-100/50 p-3 rounded-lg border border-primary-600/20">
                    <div className="flex items-start gap-2">
                      <Lightbulb className="text-primary-500 mt-0.5 flex-shrink-0" size={16} />
                      <p className="text-xs text-primary-300">
                        <strong>Need help?</strong> Click the "Hint" button on each puzzle for detailed step-by-step instructions on how to analyze documents with Claude AI.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            /* Terminal Section for Technical Rooms */
            <Card variant="elevated" className="border-2 border-primary-600/30 mb-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">{roomData.id === 'editors-chamber' ? '✏️' : '🔍'}</div>
                    <div>
                      <CardTitle>{roomData.id === 'editors-chamber' ? 'Code Editor' : 'Investigation Terminal'}</CardTitle>
                      <p className="text-sm text-primary-300 mt-1">
                        {roomData.id === 'editors-chamber'
                          ? 'Edit files to fix code - codes unlock when your fixes are correct!'
                          : 'Click files in the tree or use terminal commands - codes auto-discover when you view files!'}
                      </p>
                    </div>
                  </div>
                  <Badge variant="warning" size="sm">
                    {discoveredCodes.size}/{totalCount} codes found
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <LevelPlayground
                  starterCode={roomData.files}
                  onAction={handlePlaygroundAction}
                />
              </CardContent>
            </Card>
          )}

          {/* Puzzle Grid - Visual Lock Display */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {puzzles.map((puzzle, index) => (
              <Card
                key={puzzle.id}
                variant={puzzle.completed ? 'elevated' : 'outline'}
                className={`relative overflow-hidden transition-all duration-500 ${
                  puzzle.completed
                    ? 'border-success-600/50 bg-gradient-to-br from-success-900/20 to-transparent'
                    : discoveredCodes.has(puzzle.id)
                    ? 'border-warning-500/50 bg-gradient-to-br from-warning-900/20 to-transparent animate-pulse'
                    : pulsingLocks.has(puzzle.id)
                    ? 'border-warning-500/50 animate-pulse'
                    : 'border-primary-600/30 hover:border-primary-500/50'
                } ${recentlyCompleted === puzzle.id ? 'animate-bounce' : ''}`}
              >
                {/* Decorative corner */}
                <div className={`absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl ${
                  puzzle.completed
                    ? 'from-success-600/20'
                    : discoveredCodes.has(puzzle.id)
                    ? 'from-warning-600/20'
                    : 'from-primary-600/20'
                } to-transparent`}></div>

                <CardHeader>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="text-2xl font-bold text-primary-500/50">
                        #{index + 1}
                      </div>
                      <Badge
                        variant={puzzle.completed ? 'success' : (discoveredCodes.has(puzzle.id) && !isNonTechnicalRoom) ? 'warning' : 'default'}
                        size="sm"
                      >
                        {puzzle.completed
                          ? 'Unlocked'
                          : isNonTechnicalRoom
                          ? puzzle.type
                          : discoveredCodes.has(puzzle.id)
                          ? (roomData.id === 'file-vault' && puzzle.id === 'puzzle-3' ? 'Codes Found!' : 'Code Found!')
                          : puzzle.type}
                      </Badge>
                    </div>
                    <div className="relative">
                      {getLockIcon(puzzle)}
                      {recentlyCompleted === puzzle.id && (
                        <div className="absolute inset-0 animate-ping">
                          <Sparkles className="text-success-400" size={32} />
                        </div>
                      )}
                    </div>
                  </div>
                  <CardTitle className="text-lg">{puzzle.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-primary-200 mb-4 text-sm leading-relaxed">
                    {isNonTechnicalRoom
                      ? /* Show simplified description for non-technical rooms */
                        puzzle.description.split('\n\n')[0].replace(/\*\*/g, '').trim()
                      : /* Show full description for technical rooms */
                        puzzle.description
                    }
                  </p>

                  {/* Progress indicator for File Vault puzzle-3 */}
                  {roomData.id === 'file-vault' && puzzle.id === 'puzzle-3' && !puzzle.completed && (
                    <div className="mb-3 p-2 bg-dark-100 rounded border border-primary-600/20">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-primary-300">Security files opened:</span>
                        <span className={`font-semibold ${securityFilesOpened.size === 2 ? 'text-success-400' : 'text-warning-400'}`}>
                          {securityFilesOpened.size}/2
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <div className={`flex-1 h-1.5 rounded ${securityFilesOpened.has('authenticate.js') ? 'bg-success-500' : 'bg-dark-200'}`} />
                        <div className={`flex-1 h-1.5 rounded ${securityFilesOpened.has('hash.js') ? 'bg-success-500' : 'bg-dark-200'}`} />
                      </div>
                    </div>
                  )}

                  {/* Difficulty indicator */}
                  <div className="flex items-center gap-1 mb-4">
                    <span className="text-xs text-primary-300">Difficulty:</span>
                    {[...Array(5)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-2 h-2 rounded-full ${
                          i < puzzle.difficulty ? 'bg-primary-500' : 'bg-dark-200'
                        }`}
                      />
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant={puzzle.completed ? 'ghost' : 'outline'}
                      size="sm"
                      onClick={() => setShowHint(puzzle.id)}
                      disabled={puzzle.completed}
                      className="flex items-center gap-2"
                    >
                      <Lightbulb size={14} />
                      Hint
                    </Button>
                    {puzzle.completed && (
                      <div className="flex-1 text-center text-success-400 text-sm font-semibold py-2 flex items-center justify-center gap-2">
                        <CheckCircle size={16} />
                        Unlocked!
                      </div>
                    )}
                    {!puzzle.completed && (
                      <Button
                        variant={discoveredCodes.has(puzzle.id) || isNonTechnicalRoom ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => handleCodeEntry(puzzle.id)}
                        className="flex-1"
                        disabled={false}
                      >
                        {isNonTechnicalRoom ? (
                          <>
                            <Key size={14} className="mr-1" />
                            Enter Code
                          </>
                        ) : discoveredCodes.has(puzzle.id) ? (
                          <>
                            <Key size={14} className="mr-1" />
                            Enter {roomData.id === 'file-vault' && puzzle.id === 'puzzle-3' ? 'Codes' : 'Code'}
                          </>
                        ) : (
                          <>
                            <Lock size={14} className="mr-1" />
                            Find {roomData.id === 'file-vault' && puzzle.id === 'puzzle-3' ? 'Codes' : 'Code'} First
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Hint Modal */}
          <Modal
            isOpen={showHint !== null}
            onClose={() => setShowHint(null)}
            title={isNonTechnicalRoom ? "📚 Step-by-Step Guide" : "💡 Detective's Hint"}
            size={isNonTechnicalRoom ? "lg" : "md"}
          >
            {showHint && (
              <div className="space-y-4">
                {isNonTechnicalRoom ? (
                  /* Step-by-step guidance for non-technical rooms */
                  (() => {
                    const steps = getCurrentPuzzleSteps();
                    const currentStep = steps[currentHintStep];
                    const puzzle = puzzles.find((p) => p.id === showHint);

                    return (
                      <div className="space-y-4">
                        {/* Progress indicator */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-sm text-primary-300">
                            Step {currentHintStep + 1} of {steps.length}
                          </div>
                          <div className="flex gap-1">
                            {steps.map((_, idx) => (
                              <div
                                key={idx}
                                className={`h-2 w-8 rounded-full transition-colors ${
                                  idx === currentHintStep
                                    ? 'bg-primary-500'
                                    : idx < currentHintStep
                                    ? 'bg-success-500'
                                    : 'bg-dark-200'
                                }`}
                              />
                            ))}
                          </div>
                        </div>

                        {/* Current step content */}
                        {currentStep && (
                          <div className="bg-gradient-to-br from-primary-900/30 to-transparent p-4 rounded-lg border border-primary-600/30 min-h-[250px]">
                            <h3 className="text-lg font-semibold text-primary-400 mb-3">
                              {currentStep.title}
                            </h3>
                            <div className="text-primary-200 leading-relaxed">
                              {currentStep.type === 'list' ? (
                                <div className="space-y-3">
                                  {currentStep.content.split(/\n\n+/).filter(section => {
                                    const trimmed = section.trim();
                                    // Filter out sections that are just numbers or very short
                                    return trimmed.length > 5 && !/^\d+\.?\s*$/.test(trimmed);
                                  }).map((section, idx) => {
                                    // Check if this section has a bold header
                                    const boldMatch = section.match(/^\*\*([^*]+)\*\*\s*([\s\S]*)/);

                                    if (boldMatch) {
                                      const [, boldTitle, boldContent] = boldMatch;
                                      return (
                                        <div key={idx} className="pl-2 border-l-2 border-primary-600/30">
                                          <div className="font-semibold text-primary-300 mb-1 text-sm">
                                            {boldTitle.trim()}
                                          </div>
                                          <div className="text-sm text-primary-200 space-y-1">
                                            {boldContent.trim().split('\n').filter(line => {
                                              const trimmed = line.trim();
                                              // Filter out lines that are just numbers
                                              return trimmed.length > 0 && !/^\d+\.?\s*$/.test(trimmed);
                                            }).map((line, lineIdx) => {
                                              const trimmedLine = line.trim();

                                              // Handle bullet points
                                              if (trimmedLine.startsWith('-')) {
                                                return (
                                                  <div key={lineIdx} className="flex gap-2 items-start ml-2">
                                                    <span className="text-primary-500 mt-0.5">•</span>
                                                    <span>{trimmedLine.substring(1).trim()}</span>
                                                  </div>
                                                );
                                              }

                                              return <div key={lineIdx}>{trimmedLine}</div>;
                                            })}
                                          </div>
                                        </div>
                                      );
                                    }

                                    // Regular numbered list item
                                    const numberMatch = section.match(/^\d+\.\s*(.+)/);
                                    if (numberMatch) {
                                      const content = numberMatch[1].trim();
                                      // Only show if there's actual content after the number
                                      if (content.length > 2) {
                                        return (
                                          <div key={idx} className="text-sm">
                                            {content}
                                          </div>
                                        );
                                      }
                                      return null;
                                    }

                                    // Regular text - skip if it's just a number
                                    const trimmed = section.trim();
                                    if (!/^\d+\.?\s*$/.test(trimmed)) {
                                      return (
                                        <div key={idx} className="text-sm">
                                          {trimmed}
                                        </div>
                                      );
                                    }
                                    return null;
                                  })}
                                </div>
                              ) : (
                                <div className="text-sm space-y-2">
                                  {currentStep.content.split('\n\n').filter(para => {
                                    const trimmed = para.trim();
                                    // Filter out paragraphs that are just numbers or very short
                                    return trimmed.length > 5 && !/^\d+\.?\s*$/.test(trimmed);
                                  }).map((para, idx) => (
                                    <p key={idx} className="whitespace-pre-wrap">
                                      {para.trim()}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Navigation buttons */}
                        <div className="flex items-center justify-between pt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentHintStep(Math.max(0, currentHintStep - 1))}
                            disabled={currentHintStep === 0}
                          >
                            ← Previous
                          </Button>
                          <div className="text-xs text-primary-400">
                            {currentHintStep === steps.length - 1 ? '✓ Last step' : 'Continue reading →'}
                          </div>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => setCurrentHintStep(Math.min(steps.length - 1, currentHintStep + 1))}
                            disabled={currentHintStep === steps.length - 1}
                          >
                            Next →
                          </Button>
                        </div>

                        {/* Quick hint at the bottom (always visible) */}
                        <div className="bg-warning-900/20 border border-warning-600/30 p-3 rounded-lg">
                          <div className="flex items-start gap-2">
                            <Lightbulb className="text-warning-400 mt-0.5 flex-shrink-0" size={16} />
                            <div>
                              <div className="font-semibold text-warning-400 text-sm mb-1">Quick Hint:</div>
                              <p className="text-primary-200 text-sm">
                                {puzzle?.hint}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  /* Simple hint for technical rooms */
                  <>
                    <div className="bg-gradient-to-br from-primary-900/30 to-transparent p-6 rounded-lg border border-primary-600/30">
                      <p className="text-primary-200 leading-relaxed">
                        {puzzles.find((p) => p.id === showHint)?.hint}
                      </p>
                    </div>
                    <div className="text-sm text-primary-300 italic text-center">
                      Use this clue wisely...
                    </div>
                  </>
                )}
              </div>
            )}
            <ModalFooter>
              <Button variant="primary" onClick={() => setShowHint(null)}>
                {isNonTechnicalRoom ? 'Got it! Let\'s analyze! 🔍' : 'Got it! 🎯'}
              </Button>
            </ModalFooter>
          </Modal>

          {/* Completion Modal */}
          <Modal
            isOpen={isCompleted}
            onClose={() => {}}
            title=""
            size="lg"
            closeOnOverlayClick={false}
          >
            <div className="text-center space-y-6">
              {/* Victory Animation */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-success-600/20 via-success-500/20 to-success-600/20 blur-2xl animate-pulse"></div>
                <div className="relative">
                  <div className="text-8xl mb-4 animate-bounce">🎉</div>
                  <div className="text-6xl mb-4">🔓</div>
                </div>
              </div>

              <div>
                <h3 className="text-3xl font-bold text-success-400 mb-2">
                  Vault Unlocked!
                </h3>
                <p className="text-xl text-primary-300">You've Escaped!</p>
              </div>

              {/* Character Congratulation */}
              {character && (
                <div className="flex items-start gap-4 bg-dark-100 p-6 rounded-lg border-2 border-success-600/30">
                  <div className="text-6xl">{character.avatar}</div>
                  <div className="flex-1 text-left">
                    <div className="font-semibold text-success-400 mb-2 text-lg">
                      {character.name}
                    </div>
                    <p className="text-primary-200 italic leading-relaxed">
                      "{roomData.narrative.completion}"
                    </p>
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-6 bg-gradient-to-br from-dark-100 to-dark-50 rounded-lg border border-primary-600/30">
                  <Clock className="mx-auto mb-2 text-primary-500" size={32} />
                  <div className="text-sm text-primary-300 mb-1">Time Remaining</div>
                  <div className="text-3xl font-bold text-primary-400">
                    {formatTime(timeRemaining)}
                  </div>
                </div>
                <div className="p-6 bg-gradient-to-br from-success-900/30 to-dark-50 rounded-lg border border-success-600/30">
                  <Sparkles className="mx-auto mb-2 text-success-500" size={32} />
                  <div className="text-sm text-primary-300 mb-1">XP Earned</div>
                  <div className="text-3xl font-bold text-success-400">
                    +{earnedXP}
                  </div>
                  {bonusXP > 0 && (
                    <div className="text-xs text-warning-400 mt-1">
                      +{bonusXP} Time Bonus! ⚡
                    </div>
                  )}
                </div>
              </div>
            </div>

            <ModalFooter>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/escape-room')}
              >
                ← Back to Rooms
              </Button>
              <Button
                variant="primary"
                size="lg"
                onClick={() => window.location.reload()}
                className="bg-gradient-to-r from-success-600 to-success-500"
              >
                Play Again 🔄
              </Button>
            </ModalFooter>
          </Modal>
        </>
      )}
    </div>
  );
}
